import events from '../../constants/events';
import { debounce } from '../../utilities/functions';

import { Base } from './Base';

import { IvyPinch } from '../../plugins/ivypinch';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';

const classes = {
  bodyClass: 'zoom-open',
  open: 'open',
  pinchZoomWrapper: 'zoom__pinch-zoom-wrapper',
  pinchZoomContainer: 'zoom__pinch-zoom-container',
  zoomMove: 'zoom--move',
  zoomPinchZoom: 'zoom--pinch-zoom'
};

export class Zoom extends Base {
  constructor(behaviorName, element) {
    super(behaviorName, element);
    this.init();
  }

  init() {
    this.bindOpenListener();
    this.bindCloseListener();
  }

  bindOpenListener() {
    this.openHandler = this.openZoom.bind(this);
    window.addEventListener(events.OPEN_ZOOM, this.openHandler);
  }

  bindCloseListener() {
    const { close } = this.refs;

    if (close) {
      close.addEventListener('click', (e) => {
        e.preventDefault();
        this.closeZoom();
      });
    }
  }

  openZoom(e) {
    const { contentMove, contentPinchZoom } = this.refs;
    const { eventType, image } = e.detail;

    document.body.classList.add(classes.bodyClass);
    if (contentMove && contentPinchZoom && image) {
      // Setup move
      contentMove.innerHTML = null;

      this.imageMove = image.cloneNode();

      this.imageMove.addEventListener('load', () => {
        this.coverImageMove(this.imageMove);
      });

      // Add image
      contentMove.appendChild(this.imageMove);

      // Bind mouse handlers
      this.mouseHandler = this.moveImage.bind(this);
      this.imageMove.addEventListener('mouseover', this.mouseHandler);
      window.addEventListener('mousemove', this.mouseHandler);

      // Bind close listener
      this.imageMove.addEventListener('click', (e) => {
        e.preventDefault();
        this.closeZoom();
      });

      // Bind resize handler
      this.moveResizeHandler = debounce(() => {
        this.coverImageMove(this.imageMove);
      }, 250);

      window.addEventListener('resize', this.moveResizeHandler);

      // Setup pinch zoom
      contentPinchZoom.innerHTML = `<div class="${classes.pinchZoomWrapper}"><div class="${classes.pinchZoomContainer}"></div></div>`;

      const pinchZoomWrapper = contentPinchZoom.querySelector('.' + classes.pinchZoomWrapper);
      const pinchZoomContainer = contentPinchZoom.querySelector('.' + classes.pinchZoomContainer);

      this.imagePinchZoom = image.cloneNode();

      this.imagePinchZoom.addEventListener('load', () => {
        this.coverImagePinchZoom(this.imagePinchZoom, pinchZoomWrapper);
      });

      // Add image
      pinchZoomContainer.appendChild(this.imagePinchZoom);

      // Init pinch zoom
      this.ivyPinch = new IvyPinch({
        element: pinchZoomContainer
      });

      // Bind resize handler
      this.pinchZoomResizeHandler = debounce(() => {
        this.coverImagePinchZoom(this.imagePinchZoom, pinchZoomWrapper);
      }, 250);

      window.addEventListener('resize', this.pinchZoomResizeHandler);

      // Disable body scroll
      disableBodyScroll(this.element);

      // Bind escape key handler
      this.escapeKeyHandler = (e) => {
        if (e.key === 'Escape' || e.key === 'Esc') {
          this.closeZoom();
        }
      };

      document.addEventListener('keyup', this.escapeKeyHandler);

      // Open zoom
      if (eventType === 'click') {
        this.element.classList.add(classes.zoomMove);
      } else {
        this.element.classList.add(classes.zoomPinchZoom);
      }

      this.element.classList.add(classes.open);
    }
  }

  closeZoom() {
    // Close zoom
    document.body.classList.remove(classes.bodyClass);
    this.element.classList.remove(classes.open);
    this.element.classList.remove(classes.zoomMove);
    this.element.classList.remove(classes.zoomPinchZoom);

    // Re-enable body scroll
    enableBodyScroll(this.element);

    // Unbind resize handlers
    window.removeEventListener('resize', this.moveResizeHandler);
    window.addEventListener('resize', this.pinchZoomResizeHandler);

    // Unbind mouse handler
    window.addEventListener('mousemove', this.mouseHandler);

    // Unbind escape key handler
    document.removeEventListener('keyup', this.escapeKeyHandler);
  }

  coverImageMove(image) {
    if ((image.naturalWidth / image.naturalHeight) > (window.innerWidth / window.innerHeight)) {
      image.style.width = 'auto';
      image.style.height = '100%';
    } else {
      image.style.width = '100%';
      image.style.height = 'auto';
    }

    const boundingRect = image.getBoundingClientRect();

    const currentXDifference = window.innerWidth - boundingRect.width;
    const initialXTranslation = currentXDifference / 2;

    image.style.transform = `translate(${initialXTranslation}px, 0px)`;
  }

  coverImagePinchZoom(image, wrapper) {
    let wrapperHeight = 0;
    let wrapperWidth = 0;

    if ((image.naturalWidth / image.naturalHeight) > (window.innerWidth / window.innerHeight)) {
      wrapperHeight = window.innerHeight;
      wrapperWidth = (image.naturalWidth / image.naturalHeight) * window.innerHeight;
    } else {
      wrapperHeight = (image.naturalHeight / image.naturalWidth) * window.innerWidth;
      wrapperWidth = window.innerWidth;
    }

    const currentXDifference = window.innerWidth - wrapperWidth;
    const initialXTranslation = currentXDifference / 2;

    wrapper.style.height = `${wrapperHeight}px`;
    wrapper.style.width = ` ${wrapperWidth}px`;
    wrapper.style.transform = `translate(${initialXTranslation}px, 0px)`;
  }

  moveImage(e) {
    const boundingRect = this.imageMove.getBoundingClientRect();

    const currentXRatio = e.clientX / window.innerWidth;
    const currentYRatio = e.clientY / window.innerHeight;

    const currentXDifference = window.innerWidth - boundingRect.width;
    const currentYDifference = window.innerHeight - boundingRect.height;
    
    const transformXAmount = currentXDifference * currentXRatio;
    const transformYAmount = currentYDifference * currentYRatio;

    this.imageMove.style.transform = `translate(${transformXAmount}px, ${transformYAmount}px)`;
  }

  cleanup() {
    window.removeEventListener(events.OPEN_OVERLAY, this.openHandler);
    window.removeEventListener('resize', this.moveResizeHandler);
    window.removeEventListener('resize', this.pinchZoomResizeHandler);
    window.removeEventListener('mousemove', this.mouseHandler);
    document.removeEventListener('keyup', this.escapeKeyHandler);
    enableBodyScroll(this.element);
  }
}
